/* eslint-disable no-param-reassign */
import deselectCurrent from './toggle-selection';
import { isFirefox, isSafari } from '../../common/utils/browser-detector';
import { iOS, ipadOS } from '../../common/utils/iOSUtil';

const defaultMessage = 'Copy to clipboard: #{key}';

const isIOS = iOS() || ipadOS();

function format(message) {
  let copyKey = `${/mac os x/i.test(navigator.userAgent) ? '⌘' : 'Ctrl'}+C, Enter`;
  if (isIOS) copyKey = 'Long-tap the input field below and tap Copy';
  return message.replace(/#{\s*key\s*}/g, copyKey);
}

function stripHtml(html) {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function createMarkElForText(text) {
  const mark = document.createElement('span');
  mark.textContent = text;
  // reset user styles for span element
  mark.style.all = 'unset';
  // prevents scrolling to the end of the page
  mark.style.position = 'fixed';
  mark.style.top = 0;
  mark.style.clip = 'rect(0, 0, 0, 0)';
  // used to preserve spaces and line breaks
  mark.style.whiteSpace = 'pre';
  // do not inherit user-select (it may be `none`)
  mark.style.webkitUserSelect = 'text';
  mark.style.MozUserSelect = 'text';
  mark.style.msUserSelect = 'text';
  mark.style.userSelect = 'text';
  return mark;
}

function createMarkElForHtml(html) {
  const mark = document.createElement('div');
  mark.style.fontSize = '12pt'; // Prevent zooming on iOS
  // Reset box model
  mark.style.border = '0';
  mark.style.padding = '0';
  mark.style.margin = '0';
  // Move element out of screen
  mark.style.position = 'fixed';
  mark.style.right = '-9999px';
  mark.style.top = `${window.pageYOffset || document.documentElement.scrollTop}px`;
  // more hiding
  mark.setAttribute('readonly', '');
  mark.style.opacity = 0;
  mark.style.pointerEvents = 'none';
  mark.style.zIndex = -1;
  mark.setAttribute('tabindex', '0'); // so it can be focused
  mark.innerHTML = html;
  return mark;
}

function copy(textOrHtml, options) {
  let text;
  let message;
  let reselectPrevious;
  let range;
  let selection;
  let mark;
  let success = false;
  if (!options) {
    options = {};
  }
  const debug = options.debug || false;
  options.onlyHtml = options.onlyHtml && options.asHtml;
  options.canUsePrompt = options.canUsePrompt !== undefined ? options.canUsePrompt : true;
  try {
    reselectPrevious = deselectCurrent();

    range = document.createRange();
    selection = document.getSelection();

    let html;
    if (options.asHtml) {
      html = textOrHtml;
      text = stripHtml(html);
    } else {
      text = textOrHtml;
    }
    mark = options.asHtml ? createMarkElForHtml(html) : createMarkElForText(text);
    if (isFirefox || isSafari) {
      mark.addEventListener('copy', e => {
        e.stopPropagation();
        if (options.asHtml) {
          e.preventDefault();
          e.clipboardData.clearData();
          e.clipboardData.setData('text/html', html);
        }
      });
    }
    document.body.appendChild(mark);

    // range.setStartBefore(mark.firstChild);
    // range.setEndAfter(mark.lastChild);
    range.selectNodeContents(mark);

    selection.removeAllRanges();
    selection.addRange(range);

    const successful = document.execCommand('copy');
    if (!successful) {
      throw new Error('copy command was unsuccessful');
    }
    success = true;
  } catch (err) {
    console.log('DEBUG: fail to copy properly');
    if (debug) console.error('unable to copy using execCommand: ', err);
    if (!options.onlyHtml) {
      if (debug) console.warn('trying IE specific stuff');
      try {
        // html is not supported in IE
        // https://msdn.microsoft.com/en-us/library/ms536744(v=VS.85).aspx
        window.clipboardData.setData('text', text);
        success = true;
      } catch (error) {
        if (debug) console.error('unable to copy using clipboardData: ', error);
        if (options.canUsePrompt) {
          if (debug) console.error('falling back to prompt');
          message = format('message' in options ? options.message : defaultMessage);
          // eslint-disable-next-line no-alert
          window.prompt(message, text);
        }
      }
    }
  } finally {
    if (selection) {
      if (typeof selection.removeRange === 'function') {
        selection.removeRange(range);
      } else {
        selection.removeAllRanges();
      }
    }

    if (mark) {
      document.body.removeChild(mark);
    }
    reselectPrevious();
  }

  return success;
}

export default copy;
